import { apiClient } from '../utils/api';

export type TattooStyle =
  | 'TRADITIONAL' | 'REALISM' | 'WATERCOLOR' | 'GEOMETRIC' | 'MINIMALIST'
  | 'BLACKWORK' | 'DOTWORK' | 'TRIBAL' | 'JAPANESE' | 'BIOMECHANICAL';

export type ColorType = 'BLACK_GREY' | 'COLOR' | 'MIXED';

export type ComplexityLevel = 'SIMPLE' | 'MODERATE' | 'COMPLEX' | 'MASTERPIECE';

export interface TattooSize {
  width?: number;
  height?: number;
  unit?: string;
}

export interface TattooPlacement {
  specific?: string;
  side?: string;
}

export interface TattooRecord {
  id: string;
  actId: string;
  clientId: string;
  practitionerId?: string | null;
  appointmentId?: string | null;
  bodyZoneId?: string | null;
  style?: TattooStyle | null;
  colorType?: ColorType | null;
  complexity?: ComplexityLevel | null;
  size?: TattooSize | null;
  placement?: TattooPlacement | null;
  designImages: string[];
  sessionNotes?: string | null;
  isCompleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertTattooRecordData {
  bodyZoneId?: string;
  style?: TattooStyle;
  colorType?: ColorType;
  complexity?: ComplexityLevel;
  size?: TattooSize;
  placement?: TattooPlacement;
  designImages?: string[];
  sessionNotes?: string;
  isCompleted?: boolean;
}

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  errors?: string[];
  data?: T;
}

export async function getTattooRecord(actId: string): Promise<TattooRecord | null> {
  const response = await apiClient(`/api/planning/acts/${actId}/tattoo-record`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to fetch tattoo record');
  }
  const data: ApiResponse<{ tattooRecord: TattooRecord | null }> = await response.json();
  return data.data?.tattooRecord ?? null;
}

export async function upsertTattooRecord(
  actId: string,
  payload: UpsertTattooRecordData,
): Promise<TattooRecord> {
  const response = await apiClient(`/api/planning/acts/${actId}/tattoo-record`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const err: any = new Error(errorData.message || 'Failed to save tattoo record');
    if (Array.isArray(errorData.errors)) err.errors = errorData.errors;
    throw err;
  }

  const data: ApiResponse<{ tattooRecord: TattooRecord }> = await response.json();
  if (!data.success || !data.data?.tattooRecord) {
    throw new Error(data.message || 'Failed to save tattoo record');
  }
  return data.data.tattooRecord;
}
